// frontend/src/components/client/ClientChat.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, User, Clock, CheckCheck, Phone, Video } from 'lucide-react';
import { getClientMessages, sendClientMessage } from '../../services/clientApi';
import { useClientAuth } from '../../contexts/ClientAuthContext';

const ClientChat = () => {
  const { client } = useClientAuth();
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [staffOnline, setStaffOnline] = useState(false);
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    loadMessages();
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await getClientMessages();
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const connectWebSocket = () => {
    const token = localStorage.getItem('client_access_token');
    const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
    const wsBase = API_BASE.replace(/^http/, 'ws');
    const wsUrl = `${wsBase}/client/chat/ws?token=${token}`;
    
    wsRef.current = new WebSocket(wsUrl);
    
    wsRef.current.onopen = () => {
      console.log('WebSocket connected');
      setStaffOnline(true);
    };
    
    wsRef.current.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        setMessages(prev => [...prev, {
          id: data.id,
          message: data.message,
          sender_type: data.sender_type,
          created_at: data.created_at,
          is_read: data.is_read
        }]);
      } catch (e) {
        console.error('Error parsing message:', e);
      }
    };
    
    wsRef.current.onclose = () => {
      console.log('WebSocket disconnected');
      setStaffOnline(false);
      setTimeout(connectWebSocket, 3000);
    };
    
    wsRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || sending) return;
    
    setSending(true);
    const tempId = `temp_${Date.now()}`;
    const tempMessage = {
      id: tempId,
      message: inputMessage,
      sender_type: 'client',
      created_at: new Date().toISOString(),
      is_read: false,
      is_temp: true
    };
    
    setMessages(prev => [...prev, tempMessage]);
    setInputMessage('');
    scrollToBottom();
    
    try {
      const response = await sendClientMessage(inputMessage);
      setMessages(prev => prev.map(msg => 
        msg.id === tempId ? { ...response.data, is_temp: false } : msg
      ));
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages(prev => prev.filter(msg => msg.id !== tempId));
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)} min ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString();
  };

  return (
    <div className="flex flex-col h-[calc(100vh-200px)] px-4 sm:px-0">
      {/* Chat Header */}
      <div className="bg-gray-800/50 rounded-t-xl border border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <MessageSquare className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h2 className="text-white font-semibold">FBO Support</h2>
              <div className="flex items-center gap-1 mt-0.5">
                <div className={`w-2 h-2 rounded-full ${staffOnline ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
                <span className="text-xs text-gray-400">
                  {staffOnline ? 'Staff online' : 'Staff offline'}
                </span>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 transition">
              <Phone className="w-4 h-4" />
            </button>
            <button className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 transition">
              <Video className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 bg-gray-800/30 border-x border-gray-700 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No messages yet</p>
            <p className="text-xs mt-1">Send a message to start the conversation</p>
          </div>
        ) : (
          messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender_type === 'client' ? 'justify-end' : 'justify-start'}`}
            >
              <div className={`max-w-[80%] sm:max-w-[70%] ${msg.sender_type === 'client' ? 'items-end' : 'items-start'}`}>
                {msg.sender_type !== 'client' && (
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center">
                      <User className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs font-medium text-blue-400">FBO Support</span>
                    <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
                  </div>
                )}
                <div
                  className={`rounded-lg px-4 py-2 ${
                    msg.sender_type === 'client'
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-700 text-gray-200'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{msg.message}</p>
                </div>
                {msg.sender_type === 'client' && (
                  <div className="flex items-center gap-1 mt-1">
                    <span className="text-xs text-gray-500">{formatTime(msg.created_at)}</span>
                    {!msg.is_temp && (
                      <CheckCheck className={`w-3 h-3 ${msg.is_read ? 'text-blue-400' : 'text-gray-500'}`} />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="bg-gray-800/50 rounded-b-xl border border-gray-700 p-4">
        <div className="flex items-center gap-2">
          <textarea
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Type your message..."
            rows="1"
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2 text-white resize-none focus:outline-none focus:border-blue-500 text-sm"
          />
          <button
            onClick={sendMessage}
            disabled={sending || !inputMessage.trim()}
            className="p-2 bg-blue-500 hover:bg-blue-600 rounded-lg text-white disabled:opacity-50 transition"
          >
            {sending ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Send className="w-5 h-5" />
            )}
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Our team typically responds within 15 minutes during business hours
        </p>
      </div>
    </div>
  );
};

export default ClientChat;